import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Download, Share2, Copy, X } from "lucide-react";
import { toast } from "sonner";
import { tierMeta } from "@/lib/tiers";


/**
 * ShareableScoreCard — generates a beautiful PNG card for social sharing.
 * Uses a hidden rendered div + html2canvas for pixel-perfect export.
 */
export default function ShareableScoreCard({ score, tier, confidence, name, signals, onClose }) {
  const cardRef = useRef(null);
  const [generating, setGenerating] = useState(false);

  const tierColor = tierMeta(tier).color;
  const topSignals = Object.entries(signals || {})
    .sort((a, b) => (b[1]?.score || 0) - (a[1]?.score || 0))
    .slice(0, 3);

  const SIGNAL_LABELS = {
    academic: "Academic", fee_payment: "Fee Payment", developer_professional: "Developer",
    social_creator: "Social", coverage_consistency: "Coverage", consumption: "Consumption",
  };

  const generateImage = async () => {
    if (!cardRef.current) return;
    setGenerating(true);
    try {
      // Dynamic import to keep bundle light
      const html2canvas = (await import("html2canvas")).default;
      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: "#030712",
        scale: 2,
        useCORS: true,
        logging: false,
      });
      const url = canvas.toDataURL("image/png");

      // Download
      const link = document.createElement("a");
      link.download = `genzcredit-${score}-${tier.toLowerCase()}.png`;
      link.href = url;
      link.click();
      toast.success("Score card downloaded!");
    } catch (err) {
      console.error("Share card generation failed:", err);
      toast.error("Failed to generate share card");
    }
    setGenerating(false);
  };

  const copyToClipboard = async () => {
    if (!cardRef.current) return;
    setGenerating(true);
    try {
      const html2canvas = (await import("html2canvas")).default;
      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: "#030712",
        scale: 2,
        useCORS: true,
        logging: false,
      });
      canvas.toBlob(async (blob) => {
        if (blob) {
          await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })]);
          toast.success("Score card copied to clipboard!");
        }
      });
    } catch {
      toast.error("Failed to copy — try downloading instead");
    }
    setGenerating(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="relative max-w-md w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute -top-3 -right-3 z-10 bg-white/10 rounded-full p-1.5 hover:bg-white/20 transition-all"
        >
          <X size={16} className="text-white" />
        </button>

        {/* The card that gets captured */}
        <div
          ref={cardRef}
          className="rounded-2xl overflow-hidden"
          style={{ background: "linear-gradient(135deg, #030712 0%, #0f172a 50%, #1e1b4b 100%)" }}
        >
          <div className="p-8">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-slate-400 font-semibold">GenZ Credit Score</p>
                <p className="text-sm text-slate-500 mt-0.5">by GenZDealZ.ai</p>
              </div>
              <div
                className="px-3 py-1 rounded-full text-xs font-bold"
                style={{ backgroundColor: `${tierColor}20`, color: tierColor, border: `1px solid ${tierColor}40` }}
              >
                {tier}
              </div>
            </div>

            {/* Score */}
            <div className="text-center mb-6">
              {name && <p className="text-slate-400 text-sm mb-2">{name}</p>}
              <p
                className="font-mono text-7xl font-bold tracking-tighter"
                style={{ color: tierColor, textShadow: `0 0 40px ${tierColor}40` }}
              >
                {score}
              </p>
              <p className="text-slate-500 text-sm mt-1">out of 900</p>
              <p className="text-slate-400 text-xs mt-2">
                {(confidence * 100).toFixed(0)}% confidence
              </p>
            </div>

            {/* Top 3 Signals */}
            <div className="space-y-2 mb-6">
              {topSignals.map(([key, val]) => (
                <div key={key} className="flex items-center gap-3">
                  <div className="flex-1">
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-slate-400">{SIGNAL_LABELS[key] || key}</span>
                      <span className="text-slate-300 font-mono font-bold">{val?.score || 0}/100</span>
                    </div>
                    <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: `${val?.score || 0}%`,
                          background: `linear-gradient(90deg, ${tierColor}, ${tierColor}80)`,
                        }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Footer */}
            <div className="border-t border-white/5 pt-4 flex items-center justify-between">
              <p className="text-[10px] text-slate-500">credit.genzdealz.ai</p>
              <p className="text-[10px] text-slate-500">{new Date().toLocaleDateString("en-IN", { month: "short", day: "numeric", year: "numeric" })}</p>
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex gap-3 mt-4">
          <button
            onClick={generateImage}
            disabled={generating}
            className="flex-1 bg-gradient-to-r from-indigo-500 to-purple-500 text-white px-4 py-3 rounded-xl font-medium text-sm inline-flex items-center justify-center gap-2 hover:shadow-[0_0_20px_rgba(99,102,241,0.3)] transition-all disabled:opacity-50"
          >
            <Download size={16} /> {generating ? "Generating..." : "Download PNG"}
          </button>
          <button
            onClick={copyToClipboard}
            disabled={generating}
            className="flex-1 bg-white/10 text-white px-4 py-3 rounded-xl font-medium text-sm inline-flex items-center justify-center gap-2 hover:bg-white/15 transition-all disabled:opacity-50"
          >
            <Copy size={16} /> Copy to Clipboard
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
